import React from "react";
import { useParams } from "react-router-dom";
import {
  EmptyState,
  PortalLoading,
  StatusBadge,
  ParentPageHeader,
  niceDate,
  useParentPortal,
} from "./parentPortalUtils";

function ParentChildReportCardPage() {
  const { studentId, sessionId } = useParams();
  const { data, loading, reload } = useParentPortal(
    `parent-portal/children/${studentId}/results/${sessionId}/report-card/`
  );

  if (loading && !data) return <PortalLoading label="Loading report card..." />;

  const report = data?.report_card || data || {};
  const school = normalizeSchool(report, data);
  const student = normalizeStudent(report, data);
  const session = normalizeSession(report, data);
  const feeAccess = report?.fee_access || data?.fee_access || {};
  const subjects = normalizeSubjects(report);
  const generatedDate = niceDate(new Date().toISOString());

  const printReport = () => window.print();

  return (
    <div className="container-fluid py-4 student-report-page">
      <ParentPageHeader
        title="Printable Report Card"
        subtitle="Use Download / Print to save this report card as PDF."
        icon="bi-file-earmark-pdf"
        actions={
          <>
            <button className="btn btn-light border no-print" onClick={reload}>
              Refresh
            </button>
            <button className="btn btn-success no-print" onClick={printReport}>
              <i className="bi bi-printer me-2"></i>
              Download / Print
            </button>
          </>
        }
      />

      {!report || Object.keys(report).length === 0 ? (
        <div className="card border-0 shadow-sm">
          <EmptyState title="Report card not available" />
        </div>
      ) : (
        <div className="card border-0 shadow-sm report-card-print-area">
          <div className="card-body p-4">
            <ReportHeader school={school} generatedDate={generatedDate} />

            {school.report_header && (
              <div className="report-note mb-3">{school.report_header}</div>
            )}

            <div className="text-center mb-3">
              <h5 className="fw-bold text-uppercase mb-1">Child Report Card</h5>
              <div className="text-muted small">
                {session.name || "Academic Report"} {session.term ? `· ${session.term}` : ""}
                {session.academic_year ? ` · ${session.academic_year}` : ""}
              </div>
            </div>

            <div className="student-info-grid mb-3">
              <Info label="Student" value={student.full_name} />
              <Info label="Admission No." value={student.student_number} />
              <Info label="Class" value={student.class_group} />
              <Info label="Session" value={session.name} />
              <Info label="Academic Year" value={session.academic_year} />
              <Info label="Term" value={session.term || "-"} />
              <Info label="Overall Average" value={`${safeValue(report.overall_average, 0)}%`} />
              <Info label="Overall Grade" value={report.overall_grade || "-"} />
            </div>

            {feeAccess.has_arrears && (
              <div
                className={`alert ${
                  feeAccess.can_view_results ? "alert-warning" : "alert-danger"
                } border py-2`}
              >
                <strong>Fee access:</strong>{" "}
                {feeAccess.restriction_message || "Student has outstanding fees."}
                {feeAccess.outstanding_balance !== undefined && (
                  <span className="ms-2">
                    Outstanding balance: {feeAccess.outstanding_balance}
                  </span>
                )}
              </div>
            )}

            <div className="table-responsive">
              <table className="table table-bordered align-middle report-table">
                <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Assessments</th>
                    <th className="text-center">Average</th>
                    <th className="text-center">Grade</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {subjects.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        No marks recorded for this session.
                      </td>
                    </tr>
                  ) : (
                    subjects.map((subject, index) => (
                      <tr key={subject.class_subject || subject.subject_code || index}>
                        <td>
                          <div className="fw-semibold">{subject.subject_name || "-"}</div>
                          {subject.subject_code && (
                            <small className="text-muted">{subject.subject_code}</small>
                          )}
                        </td>
                        <td>
                          {(subject.assessments || []).length ? (
                            subject.assessments.map((assessment, assessmentIndex) => (
                              <div
                                key={assessment.assessment || assessmentIndex}
                                className="small mb-1"
                              >
                                <strong>{assessment.assessment_type || "Assessment"}:</strong>{" "}
                                {assessment.is_absent
                                  ? "Absent"
                                  : `${safeValue(assessment.raw_score, 0)}/${safeValue(
                                      assessment.max_score,
                                      "-"
                                    )}`}{" "}
                                ({safeValue(assessment.percentage, 0)}%)
                              </div>
                            ))
                          ) : (
                            <span className="text-muted small">No assessment breakdown.</span>
                          )}
                        </td>
                        <td className="text-center fw-semibold">
                          {safeValue(subject.average, 0)}%
                        </td>
                        <td className="text-center">
                          <StatusBadge value={subject.grade || "-"} />
                        </td>
                        <td>{subject.remark || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="overall-summary mt-3">
              <Info label="Overall Average" value={`${safeValue(report.overall_average, 0)}%`} />
              <Info label="Overall Grade" value={report.overall_grade || "-"} />
              <Info label="Overall Remark" value={report.overall_remark || "-"} />
              <Info
                label="Result Access"
                value={feeAccess.can_view_results === false ? "Restricted" : "Allowed"}
              />
            </div>

            {session.report_footer && (
              <div className="report-footer-note mt-3">{session.report_footer}</div>
            )}

            <div className="signature-grid mt-4">
              <SignatureBox label="Class Teacher" />
              <SignatureBox label="Head / Administrator" />
              <SignatureBox label="Parent / Guardian" />
            </div>

            <div className="printed-footer mt-3">
              <div>{school.name || "The Grange Christian School"}</div>
              <div>Printed on {generatedDate}</div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .report-card-print-area {
          max-width: 980px;
          margin: 0 auto;
          color: #111827;
        }

        .report-header {
          border-bottom: 2px solid #125b2d;
          padding-bottom: 16px;
          margin-bottom: 16px;
        }

        .report-logo {
          width: 86px;
          height: 86px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .school-name {
          color: #125b2d;
          font-size: 1.55rem;
          line-height: 1.15;
        }

        .school-motto {
          font-style: italic;
          color: #6b7280;
        }

        .report-title-box {
          border: 1px solid #d1d5db;
          border-radius: 10px;
          padding: 10px 14px;
          min-width: 170px;
          background: #f9fafb;
        }

        .report-note,
        .report-footer-note {
          border: 1px solid #d1d5db;
          background: #f9fafb;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 0.9rem;
          color: #4b5563;
        }

        .student-info-grid,
        .overall-summary {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
        }

        .report-info-box {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 9px 10px;
          min-height: 58px;
          background: #ffffff;
        }

        .report-info-label {
          color: #6b7280;
          text-transform: uppercase;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.03em;
          margin-bottom: 3px;
        }

        .report-info-value {
          font-weight: 700;
          color: #111827;
        }

        .report-table th {
          background: #f3f4f6;
          color: #111827;
          font-size: 0.82rem;
        }

        .report-table td {
          font-size: 0.88rem;
          vertical-align: top;
        }

        .signature-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 18px;
        }

        .signature-line {
          border-bottom: 1px solid #9ca3af;
          height: 34px;
          margin-bottom: 6px;
        }

        .printed-footer {
          border-top: 1px solid #d1d5db;
          padding-top: 10px;
          color: #6b7280;
          font-size: 12px;
          display: flex;
          justify-content: space-between;
          gap: 12px;
        }

        @media (max-width: 768px) {
          .student-info-grid,
          .overall-summary,
          .signature-grid {
            grid-template-columns: 1fr;
          }

          .report-header {
            align-items: flex-start !important;
          }

          .report-title-box {
            width: 100%;
          }
        }

        @media print {
          @page {
            size: A4;
            margin: 12mm;
          }

          html,
          body {
            background: #fff !important;
          }

          body * {
            visibility: hidden;
          }

          .report-card-print-area,
          .report-card-print-area * {
            visibility: visible;
          }

          .report-card-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            max-width: none;
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
          }

          .report-card-print-area .card-body {
            padding: 0 !important;
          }

          .grange-sidebar,
          .navbar,
          .no-print,
          .student-report-page > .card:not(.report-card-print-area),
          .student-report-page .page-header,
          .btn {
            display: none !important;
          }

          .school-name {
            font-size: 22px;
          }

          .report-logo {
            width: 78px;
            height: 78px;
          }

          .student-info-grid,
          .overall-summary {
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
          }

          .report-info-box {
            padding: 7px 8px;
            min-height: 50px;
          }

          .report-table th,
          .report-table td {
            padding: 6px;
            font-size: 12px;
          }

          .signature-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 14px;
          }

          .printed-footer {
            font-size: 11px;
          }
        }
      `}</style>
    </div>
  );
}

function ReportHeader({ school, generatedDate }) {
  const schoolAddress = [
    school.address,
    school.city,
    school.province,
    school.country,
  ]
    .filter(Boolean)
    .join(", ");

  const schoolContacts = [
    school.phone,
    school.alternative_phone,
    school.email,
    school.website,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <div className="report-header d-flex flex-wrap align-items-center justify-content-between gap-3">
      <div className="d-flex align-items-center gap-3">
        {school.logo_url && (
          <img className="report-logo" src={school.logo_url} alt="School logo" />
        )}
        <div>
          <h3 className="school-name fw-bold mb-1">
            {school.name || "The Grange Christian School"}
          </h3>
          {school.motto && <div className="school-motto mb-1">{school.motto}</div>}
          {schoolAddress && <div className="text-muted small">{schoolAddress}</div>}
          {schoolContacts && <div className="text-muted small">{schoolContacts}</div>}
        </div>
      </div>

      <div className="report-title-box text-md-end">
        <div className="fw-bold text-uppercase">Report Card</div>
        <small className="text-muted">Generated: {generatedDate}</small>
      </div>
    </div>
  );
}

function normalizeSchool(report, data) {
  const school =
    report?.school_profile ||
    report?.school ||
    data?.school_profile ||
    data?.school ||
    data?.report_card?.school_profile ||
    data?.report_card?.school ||
    {};

  return {
    name: school.name || "The Grange Christian School",
    short_name: school.short_name || "",
    motto: school.motto || "",
    logo_url: school.logo_url || school.logo || "",
    address: school.address || "",
    city: school.city || "",
    province: school.province || "",
    country: school.country || "",
    phone: school.phone || "",
    alternative_phone: school.alternative_phone || "",
    email: school.email || "",
    website: school.website || "",
    report_header: school.report_header || "",
    receipt_footer: school.receipt_footer || "",
  };
}

function normalizeStudent(report, data) {
  const directStudent = report?.student || data?.student || data?.profile || {};

  const firstName = directStudent.first_name || report?.first_name || data?.first_name || "";
  const lastName = directStudent.last_name || report?.last_name || data?.last_name || "";
  const combinedName = `${firstName} ${lastName}`.trim();

  return {
    full_name:
      directStudent.full_name ||
      directStudent.name ||
      report?.student_name ||
      report?.full_name ||
      data?.student_name ||
      data?.full_name ||
      combinedName ||
      "-",
    student_number:
      directStudent.student_number ||
      directStudent.admission_number ||
      report?.student_number ||
      report?.admission_number ||
      data?.student_number ||
      data?.admission_number ||
      "-",
    class_group:
      directStudent.class_group ||
      directStudent.class_group_name ||
      directStudent.current_class_group_name ||
      report?.class_group ||
      report?.class_group_name ||
      data?.class_group_name ||
      "-",
  };
}

function normalizeSession(report, data) {
  const session = report?.session || data?.session || data?.report_card?.session || {};
  return {
    name: session.name || report?.session_name || data?.session_name || "-",
    academic_year:
      session.academic_year ||
      session.academic_year_name ||
      report?.academic_year_name ||
      data?.academic_year_name ||
      "-",
    term:
      session.term ||
      session.term_name ||
      report?.term_name ||
      data?.term_name ||
      "",
    report_footer:
      session.report_footer ||
      report?.report_footer ||
      data?.report_footer ||
      "",
  };
}

function normalizeSubjects(report) {
  if (Array.isArray(report?.subjects)) return report.subjects;
  if (Array.isArray(report?.results)) return report.results;
  if (Array.isArray(report?.report_card?.subjects)) return report.report_card.subjects;
  if (Array.isArray(report?.report_card?.results)) return report.report_card.results;
  return [];
}

function safeValue(value, fallback = "-") {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
}

function Info({ label, value }) {
  return (
    <div className="report-info-box">
      <div className="report-info-label">{label}</div>
      <div className="report-info-value">{value || "-"}</div>
    </div>
  );
}

function SignatureBox({ label }) {
  return (
    <div>
      <div className="signature-line"></div>
      <div className="text-muted small text-center">{label}</div>
    </div>
  );
}

export default ParentChildReportCardPage;
